import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import type { DocGroup } from "../../features/tree/doc-groups";
import { CodeText, DocIcon, TypeChip } from "./misc";
import { Panel } from "./Panel";

/** A document below another one is a step in per level. Classes, not a style: the CSP refuses inline styles. */
const INDENT = ["", "pl-6", "pl-12", "pl-18"];

type DocTableProps = {
  label: string;
  groups: DocGroup[];
  titleHeading: string;
  showType?: boolean;
  headings?: "auto" | "always";
  emptyNote?: string;
};

/**
 * A list of documents: one table in a panel (page frame spec §4.3). Each group is a `tbody` with its heading as a
 * row across the columns. `headings="auto"` leaves the heading out when there is one group only. A group without
 * documents is left out, unless `emptyNote` says what to show under its heading. Renders nothing without groups to
 * show: the page shows its own empty state.
 */
export function DocTable(props: DocTableProps) {
  const table = DocTableBody(props);
  return table ? <Panel>{table}</Panel> : null;
}

/** The table without its panel, for a page that puts a title or a filter in the panel above it. */
export function DocTableBody({
  label,
  groups,
  titleHeading,
  showType = false,
  headings = "auto",
  emptyNote,
}: DocTableProps) {
  const shown = emptyNote ? groups : groups.filter((g) => g.docs.length > 0);
  if (shown.length === 0) return null;
  const withHeadings = headings === "always" || shown.length > 1;
  const columns = showType ? 3 : 2;
  return (
    <div className="overflow-x-auto">
      <table aria-label={label} className={clsx("kd-datatable", showType ? "min-w-[640px]" : "min-w-[440px]")}>
        <thead>
          <tr>
            <th>{titleHeading}</th>
            <th>Code</th>
            {showType ? <th>Type</th> : null}
          </tr>
        </thead>
        {shown.map((g) => (
          <tbody key={g.key}>
            {withHeadings ? (
              <tr>
                <th scope="rowgroup" colSpan={columns}>
                  {g.docId ? (
                    <Link to="/d/$ref" params={{ ref: g.docId }} className="hover:text-green-ink">
                      {g.heading}
                    </Link>
                  ) : (
                    g.heading
                  )}{" "}
                  ({g.docs.length})
                </th>
              </tr>
            ) : null}
            {g.docs.length === 0 ? (
              <tr>
                <td colSpan={columns} className="text-sm text-ink-3">
                  {emptyNote}
                </td>
              </tr>
            ) : null}
            {g.docs.map((d) => (
              <tr key={d.id}>
                <td>
                  <Link
                    to="/d/$ref"
                    params={{ ref: d.id }}
                    className={clsx(
                      "flex items-center gap-3 font-semibold text-ink hover:text-green-ink",
                      INDENT[Math.min(d.depth ?? 0, INDENT.length - 1)],
                    )}
                  >
                    <DocIcon kind={d.kind} />
                    {d.title}
                  </Link>
                </td>
                <td>{d.code ? <CodeText code={d.code} /> : null}</td>
                {showType ? (
                  <td>
                    <TypeChip code={d.code} kind={d.kind} />
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}
